import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import ApiService from '../services/ApiService';

const money = (n) =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const FOCUS_DEPARTMENTS = ['MCS', 'GAM'];
const PAGE_SIZE = 500;

const emptyDept = (name) => ({
  department: name,
  workOrderCount: 0,
  invoiced: 0,
  cost: 0,
  profit: 0,
  margin: 0
});

// Fetch every work order in the period using the existing paged endpoint.
const fetchAllWorkOrders = async (year) => {
  const params = { pageSize: PAGE_SIZE };
  if (year > 0) {
    params.dateFrom = `${year}-01-01`;
    params.dateTo = `${year}-12-31T23:59:59`;
  }
  const all = [];
  for (let page = 1; ; page++) {
    const { data, pagination } = await ApiService.getWorkOrders({ ...params, page });
    all.push(...data);
    if (data.length < PAGE_SIZE || all.length >= pagination.totalCount) break;
  }
  return all;
};

const summarize = (workOrders) => {
  const groups = {};
  workOrders.forEach((w) => {
    const name = (w.department || '').trim().toUpperCase() || 'Unassigned';
    const g = (groups[name] = groups[name] ?? emptyDept(name));
    g.workOrderCount += 1;
    g.invoiced += w.invoiceAmount || 0;
    g.cost += w.totalCost || 0;
  });
  return Object.values(groups)
    .map((g) => {
      const profit = g.invoiced - g.cost;
      return { ...g, profit, margin: g.invoiced > 0 ? (profit / g.invoiced) * 100 : 0 };
    })
    .sort((a, b) => b.invoiced - a.invoiced);
};

const DepartmentRevenueReport = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setDepartments(summarize(await fetchAllWorkOrders(year)));
    } catch (err) {
      console.error('Error loading department revenue:', err);
      setError('Failed to load department revenue.');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year]);

  const byDept = Object.fromEntries(departments.map((d) => [d.department, d]));
  const focus = FOCUS_DEPARTMENTS.map((name) => byDept[name] ?? emptyDept(name));
  const combined = focus.reduce(
    (acc, d) => ({
      ...acc,
      workOrderCount: acc.workOrderCount + d.workOrderCount,
      invoiced: acc.invoiced + d.invoiced,
      cost: acc.cost + d.cost,
      profit: acc.profit + d.profit
    }),
    emptyDept('MCS + GAM')
  );
  const years = [0, ...Array.from({ length: 5 }, (_, i) => currentYear - i)];

  return (
    <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Revenue by Department</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Work orders grouped by department. Profit = invoiced minus contractor cost.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y === 0 ? 'All time' : y}
              </option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[...focus, combined].map((d) => (
          <div key={d.department} className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500">{d.department}</div>
            <div className="text-2xl font-semibold text-gray-900">{money(d.profit)}</div>
            <div className="text-xs text-gray-500">profit</div>
            <div className="mt-2 text-sm text-gray-600">
              {money(d.invoiced)} invoiced · {d.workOrderCount} work orders
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Department', 'Work Orders', 'Invoiced', 'Cost', 'Profit', 'Margin'].map((h, i) => (
                <th
                  key={h}
                  className={`px-3 py-2 font-medium text-gray-500 ${i === 0 ? 'text-left' : 'text-right'}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {departments.map((d) => (
              <tr
                key={d.department}
                className={FOCUS_DEPARTMENTS.includes(d.department) ? 'bg-blue-50' : ''}
              >
                <td className="px-3 py-2 font-medium text-gray-900">{d.department}</td>
                <td className="px-3 py-2 text-right">{d.workOrderCount}</td>
                <td className="px-3 py-2 text-right">{money(d.invoiced)}</td>
                <td className="px-3 py-2 text-right">{money(d.cost)}</td>
                <td className="px-3 py-2 text-right">{money(d.profit)}</td>
                <td className="px-3 py-2 text-right">{d.margin.toFixed(1)}%</td>
              </tr>
            ))}
            {!loading && !error && departments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-gray-500">
                  No work orders for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepartmentRevenueReport;
